import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export const useNamesFormSchema = (emailRequired = false) => {
  const { t } = useTranslation();

  return z.object({
    firstName: z.string().min(1, t('formErrors.required')),
    lastName: z.string().min(1, t('formErrors.required')),
    email: z
      .email(t('formErrors.email'))
      .optional()
      .or(z.literal(''))
      .refine(
        value => !emailRequired || (value !== undefined && value !== ''),
        t('formErrors.required'),
      )
      .transform(value => {
        if (value === '') {
          return undefined;
        }
        return value;
      }),
  });
};

export type NamesFormData = z.infer<ReturnType<typeof useNamesFormSchema>>;

export type NamesFormInput = z.input<ReturnType<typeof useNamesFormSchema>>;
export type NamesFormOutput = z.output<ReturnType<typeof useNamesFormSchema>>;

export const useNamesForm = (initialData?: Partial<NamesFormData>, emailRequired = false) => {
  const namesSchema = useNamesFormSchema(emailRequired);

  const form = useForm<NamesFormInput, unknown, NamesFormOutput>({
    resolver: zodResolver(namesSchema),
    defaultValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      email: initialData?.email || '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    form.reset({
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      email: initialData?.email || '',
    });
  }, [form, initialData]);

  return {
    form,
  };
};
